import React, { useContext, useMemo } from 'react';
import styles from './ContactForm.module.scss';
import {
  ReportConfig,
  Section,
  LocationFormValueType
} from '../../../../../Models';
import { SectionForm } from '../../SectionForm/SectionForm';
import { DateField } from '../../FieldInput/DateField/DateField';
import { TimeField } from '../../FieldInput/TimeField/TimeField';
import {
  GeneralInformationValueType,
  FieldValueType,
  SectionValueType
} from '../../../../../Models';
import { StateConfigContext } from '../../../../../Context/StateConfigContext';
import { LocationForm } from '../../LocationForm/LocationForm';
import {
  DATE_FUTURE_MSG,
  INCIDENT_DATE_YEAR_ERROR_MSG,
  TIME_FUTURE_MSG,
  TIME_HELP_MSG
} from '../../../../../Constants/Messages';
import {
  checkFutureDay,
  checkFutureTime,
  isValueEmpty
} from '../../../../../Helpers';
import { AutocompleteChipField } from '../../FieldInput/AutocompleteChipField/AutocompleteChipField';
import { RadioField } from '../../FieldInput/RadioField/RadioField';
import {
  CustomerContext,
  ReportCustomizationValue
} from 'ui-smartforce-settings';
import { getFieldConfig } from '../../../../../Services/ReportService';

const MIN_DATE = new Date('01/01/2022');

const LIGHTING_CONDITIONS_OPTIONS: string[] = [
  'Artificial Lighting (Streetlights)',
  'Artificial Lighting (Vehicle Lights)',
  'Bright Daylight',
  'Complete Darkness',
  'Overcast/Cloudy',
  'Shadowed/Partially Lit',
  'Twilight/Dawn'
];

function getDateHelperText(
  isFutureDay: boolean,
  isInvalidYear: boolean
): string {
  if (isFutureDay) {
    return DATE_FUTURE_MSG;
  } else if (isInvalidYear) {
    return INCIDENT_DATE_YEAR_ERROR_MSG;
  } else {
    return '';
  }
}

export function isValidYear(date: moment.Moment) {
  if (!date) {
    return false;
  }

  return date.year() >= 2022;
}

function isValidOption(
  reportCustomizationValue: ReportCustomizationValue,
  option: string
) {
  const fieldConfig = getFieldConfig(
    reportCustomizationValue,
    'type',
    'general_information'
  );

  return !fieldConfig?.options || fieldConfig.options.includes(option);
}

export interface ContactFormProps {
  value: GeneralInformationValueType;
  onDefaultFieldChange: (name: string, value: FieldValueType) => void;
  onSectionChange: (name: string, value: SectionValueType) => void;
}

export const ContactForm = ({
  value,
  onDefaultFieldChange,
  onSectionChange
}: ContactFormProps): React.ReactElement<ContactFormProps> => {
  const stateConfig = React.useContext(StateConfigContext)
    .stateConfig as ReportConfig;
  const { reportCustomizationValue } = useContext(CustomerContext);
  const isFutureDay: boolean = checkFutureDay(value['date'] as moment.Moment);
  const isFutureTime: boolean = checkFutureTime(
    value['date'] as moment.Moment,
    value['time'] as moment.Moment
  );

  const isInvalidYear = !isValidYear(value['date'] as moment.Moment);

  const connectReportsSection = stateConfig.general_information.sections.filter(
    (section: Section) => section.name === 'to_connect_reports'
  );

  const stateRelatedSections = stateConfig.general_information.sections.filter(
    (section: Section) => section.name !== 'to_connect_reports'
  );

  // Memoize report type options to avoid re-calculating on each render
  const reportTypeOptions: string[] = useMemo(() => {
    return ['Call For Service', 'Officer Pro-active'].filter((o) =>
      isValidOption(reportCustomizationValue, o)
    );
  }, [reportCustomizationValue]);

  // If there is only one report type option, set it as default
  if (reportTypeOptions.length === 1 && isValueEmpty(value['type'])) {
    onDefaultFieldChange('type', reportTypeOptions[0]);
  }

  return (
    <div className={styles.contactForm}>
      <h3>General Information</h3>

      <div className={styles.generalInformation}>
        <div className={styles.defaultFields}>
          {/* Connect Reports | CAD Number */}
          {connectReportsSection.length > 0 && (
            <div
              className={`${styles.inputField} ${styles.extraMarginBottom}`}
              key={connectReportsSection[0].name}
            >
              <h4>{connectReportsSection[0].label}</h4>
              <SectionForm
                parentSectionName="general_information"
                config={connectReportsSection[0]}
                isInner
                value={value.state_related[connectReportsSection[0].name]}
                onChange={(sectionValue: SectionValueType) =>
                  onSectionChange(connectReportsSection[0].name, sectionValue)
                }
              />
            </div>
          )}

          {/* Report Type */}
          <div className={`${styles.inputField} ${styles.extraMarginBottom}`}>
            <h4>Report Type *</h4>
            <RadioField
              label=""
              required
              value={value['type'] as FieldValueType}
              options={reportTypeOptions}
              onChange={(value: FieldValueType) =>
                onDefaultFieldChange('type', value)
              }
            />
          </div>

          <h4 style={{ margin: 0 }}>Date and Location</h4>
          <DateField
            error={isFutureDay || isInvalidYear}
            helperText={getDateHelperText(isFutureDay, isInvalidYear)}
            label="Date"
            minDate={MIN_DATE}
            onChange={(value: FieldValueType) =>
              onDefaultFieldChange('date', value)
            }
            required
            value={value['date'] as FieldValueType}
          />

          <TimeField
            label="Time"
            required
            value={value['time'] as FieldValueType}
            error={isFutureTime}
            helperText={isFutureTime ? TIME_FUTURE_MSG : TIME_HELP_MSG}
            onChange={(value: FieldValueType) =>
              onDefaultFieldChange('time', value)
            }
          />

          <LocationForm
            config={stateConfig.location_type}
            value={value['location'] as LocationFormValueType}
            onChange={(value) => onDefaultFieldChange('location', value)}
          />
        </div>

        <div className={styles.inputField}>
          <h4>Lighting Conditions Details</h4>
          <AutocompleteChipField
            label="Lighting Conditions"
            value={value['lighting_conditions'] as FieldValueType}
            options={LIGHTING_CONDITIONS_OPTIONS}
            onChange={(value) =>
              onDefaultFieldChange('lighting_conditions', value ?? [])
            }
          />
        </div>

        {stateRelatedSections.map((section: Section) => (
          <div key={section.name}>
            <h4>{section.label}</h4>
            <SectionForm
              parentSectionName="general_information"
              config={section}
              isInner
              value={value.state_related[section.name]}
              onChange={(sectionValue: SectionValueType) =>
                onSectionChange(section.name, sectionValue)
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};
